import { BookingState } from '../views/BookingFlow/types';
import axios from 'axios';
import {
  LANG_URL,
  BOOKED_APPOINTMENTS_URL,
  BOOKING_SLOTS_URL,
  BOOK_MEETING_URL,
  LOGOUT_URL,
  PRACTICE_SETTINGS_URL,
} from './constants';
import request from './request';
import {
  BookingResponse,
  BookingSlotsResponse,
  PracticeSettingsResponse,
  BookedAppointmentsResponse,
} from './types';

export async function getLang(): Promise<object> {
  return request<object>(LANG_URL, {}, false);
}

export async function getBookingSlots(
  startTime: number,
  endTime: number
): Promise<BookingSlotsResponse> {
  return request<BookingSlotsResponse>(
    `${BOOKING_SLOTS_URL}?start_time=${startTime}&end_time=${endTime}`,
    {
      headers: {
        Authorization: `Bearer ${window.sessionStorage.getItem('jwtToken')}`,
      },
    }
  );
}

export async function bookMeeting(
  booking: BookingState
): Promise<BookingResponse> {
  const body = {
    phone_number: booking.phoneNumber,
    nhs_number: booking.patientNhsNumber,
    gender: booking.patientGender,
    meeting_time: booking.timeSlot?.toISOString().replace('.000', ''),
    appointment_reason: booking.optionalNotes,
    is_child: booking.isChild,
    patient_full_name: booking.patientName,
    patient_date_of_birth: booking.patientDateOfBirth,
    legal_guardian_nhs_number: booking.legalGuradianNhsNumber,
    profession_id: booking.professionId,
  };

  return request<BookingResponse>(`${BOOK_MEETING_URL}`, {
    method: 'POST',
    body,
    headers: {
      Authorization: `Bearer ${window.sessionStorage.getItem('jwtToken')}`,
    },
  });
}

export async function getPracticeSettings(): Promise<PracticeSettingsResponse> {
  if (window.sessionStorage.getItem('jwtToken') == null) {
    return Promise.resolve({
      appointmentListEnabled: false,
      childBookingEnabled: false,
      partnership: 'UNKNOWN',
      isNurseBookingEnabled: false,
    });
  }

  return request<PracticeSettingsResponse>(`${PRACTICE_SETTINGS_URL}`, {
    headers: {
      Authorization: `Bearer ${window.sessionStorage.getItem('jwtToken')}`,
    },
  });
}

export async function getBookedAppointments(): Promise<
  BookedAppointmentsResponse
> {
  return request<BookedAppointmentsResponse>(`${BOOKED_APPOINTMENTS_URL}`, {
    headers: {
      Authorization: `Bearer ${window.sessionStorage.getItem('jwtToken')}`,
    },
  });
}

/**
 * Own instance: ErrorBoundary's interceptor on the default one drops the whole
 * app into its error state on any failure, and signing out is allowed to fail.
 */
const signOutClient = axios.create();

/**
 * Invalidates the token server-side; without this it stays valid however
 * thoroughly the browser forgets it. Takes the token as an argument so the
 * caller can clear storage without waiting on the network.
 *
 * Best effort: a failure must not block signing out.
 */
export async function endSession(token: string): Promise<void> {
  try {
    await signOutClient.request({
      url: LOGOUT_URL,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    console.error(
      'Could not end the session on the server; the token may stay valid until it expires',
      error
    );
  }
}
